import { Stepper, Text, Tooltip } from '@mantine/core'
import type { EnvName, EnvironmentChainStep } from '@/api/chamados'

const ENV_ORDER: EnvName[] = ['dev', 'qa', 'hml', 'prd']

const ENV_LABEL: Record<EnvName, string> = {
  dev: 'Dev',
  qa: 'QA',
  hml: 'Homologação',
  prd: 'Produção',
}

export interface EnvironmentChainProgressProps {
  /** Cadeia real da organização (só os ambientes que existem). */
  chain: EnvironmentChainStep[]
  /** Ambiente atual do chamado. */
  currentEnvironment: string | null
}

/**
 * Trilha de ambientes dev→qa→hml→prd (sub-projeto A). Passos que existem na
 * cadeia da organização aparecem ativos; ausentes ficam esmaecidos. O passo do
 * `currentEnvironment` — onde o chamado deve ser executado — é o ativo do Stepper.
 *
 * Sem ambiente, nenhum passo fica ativo: antes caía no primeiro, e o pedido de
 * criação do rota/PRD aparecia em Dev. E os passos anteriores ao atual não ganham
 * o ✓ de concluído — o Stepper os marca assim, mas a barra não sabe se o chamado
 * passou por eles (um pedido de PRD nasce direto em Produção).
 */
export function EnvironmentChainProgress({ chain, currentEnvironment }: EnvironmentChainProgressProps) {
  const present = new Set(chain.map((c) => c.environment))
  const activeIndex = currentEnvironment
    ? ENV_ORDER.indexOf(currentEnvironment as EnvName)
    : -1

  return (
    <Stepper active={activeIndex} size="sm" iconSize={28}>
      {ENV_ORDER.map((env, index) => {
        const inChain = present.has(env)
        const label = ENV_LABEL[env]
        const desc = inChain
          ? env === currentEnvironment
            ? 'Ambiente atual'
            : undefined
          : 'Fora da esteira desta organização'
        return (
          <Stepper.Step
            key={env}
            data-ambiente={env}
            data-atual={env === currentEnvironment ? 'sim' : undefined}
            completedIcon={index + 1}
            color={index < activeIndex ? 'gray' : undefined}
            label={
              <Tooltip label={desc ?? label} disabled={!desc}>
                <Text
                  size="sm"
                  fw={env === currentEnvironment ? 700 : 500}
                  c={inChain ? undefined : 'dimmed'}
                >
                  {label}
                </Text>
              </Tooltip>
            }
            description={inChain ? undefined : 'ausente'}
          />
        )
      })}
    </Stepper>
  )
}

export default EnvironmentChainProgress
